import { Alert, Badge, Button, Group, Modal, Paper, Stack, Text, Title } from "@mantine/core"
import { useEffect, useMemo, useRef, useState } from "react"
import { QuestionDetails } from "atlas_forecasting_app/components/common/question_details"
import { VisibilityBadge } from "atlas_forecasting_app/components/common/visibility_badge"
import { EmptyState, ErrorAlert, LoadingState, PageHeader, SectionCard } from "atlas_forecasting_app/components/common/page_elements"
import { FORECAST_PROGRESS_MESSAGES } from "atlas_forecasting_app/content/forecast_progress_messages"
import { ForecastDisplay } from "atlas_forecasting_app/components/forecast/forecast_display"
import { ForecastHistoryChart } from "atlas_forecasting_app/components/forecast/forecast_history_chart"
import { ForecastMainDetails } from "atlas_forecasting_app/components/forecast/forecast_main_details"
import { subscribeToQuestionForecastStatus } from "atlas_forecasting_app/lib/cable"
import { apiFetch } from "atlas_forecasting_app/lib/api_fetch"
import { formatCurrencyFromCents, formatDateTime } from "atlas_forecasting_app/lib/format"
import { RouterLink } from "atlas_forecasting_app/lib/router"
import { NotFoundPage } from "atlas_forecasting_app/pages/not_found_page"

const QUESTION_PUBLISH_CONFIRMATION =
  "Are you sure? Questions that are public are viewable by anybody. Once a question has been made public it cannot be made private again."

const FORECAST_PUBLISH_CONFIRMATION =
  "Are you sure? Forecasts that are public are viewable by anybody. Once a forecast has been made public it cannot be made private again."

const FORECAST_IN_PROGRESS_STATUSES = new Set(["queued", "running"])
const FORECAST_CONFIRMATION_MESSAGE =
  "Forecasts typically take around 15 minutes to generate. Forecasts are less accurate if information that became available within the last hour would notably impact the result or when the topic depends on information that cannot be found publicly on the internet."
const FORECAST_CTA_HEIGHT = 36
const FORECAST_PROGRESS_WORKING_MS = 2_000
const FORECAST_PROGRESS_THINKING_MS = 3_000
const FORECAST_PROGRESS_ROTATION_MS = 10_000

function timeMs(value) {
  const parsed = value ? Date.parse(value) : Number.NaN
  return Number.isNaN(parsed) ? 0 : parsed
}

function forecastFreshnessMs(forecast) {
  return (
    timeMs(forecast?.updated_at)
    || timeMs(forecast?.completed_at)
    || timeMs(forecast?.started_at)
    || timeMs(forecast?.created_at)
  )
}

function forecastStartedMs(forecast) {
  return timeMs(forecast?.started_at) || timeMs(forecast?.created_at)
}

function formatElapsedDuration(elapsedMs) {
  const totalSeconds = Math.max(0, Math.floor(elapsedMs / 1000))
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  if (hours > 0) return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
}

function positiveModulo(value, divisor) {
  if (!divisor) return 0
  return ((value % divisor) + divisor) % divisor
}

function greatestCommonDivisor(left, right) {
  let a = Math.abs(left)
  let b = Math.abs(right)

  while (b !== 0) {
    const remainder = a % b
    a = b
    b = remainder
  }

  return a
}

function forecastProgressMessageStep(messageCount, seed) {
  if (messageCount <= 1) return 0

  let step = positiveModulo(seed, messageCount - 1) + 1
  while (greatestCommonDivisor(step, messageCount) !== 1) {
    step = (step % (messageCount - 1)) + 1
  }

  return step
}

function forecastProgressMessageIndex(messageCount, windowIndex, seed) {
  if (messageCount <= 1) return 0

  const startIndex = positiveModulo((seed * 7) + 3, messageCount)
  const step = forecastProgressMessageStep(messageCount, (seed * 11) + 5)
  return positiveModulo(startIndex + (windowIndex * step), messageCount)
}

function buildForecastProgressFooter(elapsedMs, forecastSeed) {
  if (elapsedMs < FORECAST_PROGRESS_WORKING_MS) {
    return { phase: "working", text: "Working on your question..." }
  }

  const thinkingElapsedMs = elapsedMs - FORECAST_PROGRESS_WORKING_MS
  if (thinkingElapsedMs < FORECAST_PROGRESS_THINKING_MS) {
    const dotCount = Math.min(3, Math.floor(thinkingElapsedMs / 1_000) + 1)
    return { phase: "thinking", text: `Thinking${".".repeat(dotCount)}` }
  }

  const humorWindowIndex = Math.floor((thinkingElapsedMs - FORECAST_PROGRESS_THINKING_MS) / FORECAST_PROGRESS_ROTATION_MS)
  const messageIndex = forecastProgressMessageIndex(FORECAST_PROGRESS_MESSAGES.length, humorWindowIndex, forecastSeed)
  return { phase: "humor", text: FORECAST_PROGRESS_MESSAGES[messageIndex] }
}

function mergeForecasts(existingForecasts, incomingForecasts) {
  const byId = new Map()

  ;[...(existingForecasts || []), ...(incomingForecasts || [])]
    .filter((forecast) => forecast?.id != null)
    .forEach((forecast) => {
      const existing = byId.get(forecast.id)
      if (!existing || forecastFreshnessMs(forecast) >= forecastFreshnessMs(existing)) {
        byId.set(forecast.id, { ...existing, ...forecast })
      }
    })

  return Array.from(byId.values())
    .sort((left, right) => {
      const createdDelta = timeMs(right.created_at) - timeMs(left.created_at)
      if (createdDelta !== 0) return createdDelta
      return (right.id || 0) - (left.id || 0)
    })
    .slice(0, 50)
}

export function QuestionPage({ questionId, currentUser, forecastCostCents = 800, onCurrentUserUpdate }) {
  const [question, setQuestion] = useState(null)
  const [forecasts, setForecasts] = useState([])
  const [forecastDetailsById, setForecastDetailsById] = useState({})
  const [detailLoadingIds, setDetailLoadingIds] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [notFound, setNotFound] = useState(false)
  const [starting, setStarting] = useState(false)
  const [forecastConfirmationOpen, setForecastConfirmationOpen] = useState(false)
  const [publishingQuestion, setPublishingQuestion] = useState(false)
  const [publishingForecastIds, setPublishingForecastIds] = useState({})
  const [historySelectionId, setHistorySelectionId] = useState(null)
  const [elapsedTimeNowMs, setElapsedTimeNowMs] = useState(() => Date.now())
  const [readyForecastId, setReadyForecastId] = useState(null)
  const forecastsRef = useRef([])
  const previousActiveOwnedForecastIdRef = useRef(null)
  const balanceCents = currentUser?.account_balance_cents ?? 0
  const hasEnoughBalance = balanceCents >= forecastCostCents
  const forecastCostLabel = formatCurrencyFromCents(forecastCostCents)
  const balanceLabel = formatCurrencyFromCents(balanceCents)

  const mergeForecastState = (incomingForecasts) => {
    const nextForecasts = mergeForecasts(forecastsRef.current, incomingForecasts)
    forecastsRef.current = nextForecasts
    setForecasts(nextForecasts)
    setForecastDetailsById((prev) => {
      const allowedIds = new Set(nextForecasts.map((forecast) => forecast.id))
      return Object.fromEntries(Object.entries(prev).filter(([id]) => allowedIds.has(Number(id))))
    })
    return nextForecasts
  }

  const refresh = async () => {
    setLoading(true)
    try {
      const data = await apiFetch(`/api/questions/${questionId}`)
      setQuestion(data.question)
      mergeForecastState(data.forecasts || [])
      setError("")
      setNotFound(false)
    } catch (e) {
      if (e.status === 404) {
        forecastsRef.current = []
        setQuestion(null)
        setForecasts([])
        setForecastDetailsById({})
        setError("")
        setNotFound(true)
      } else {
        setError(e.message)
        setNotFound(false)
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    forecastsRef.current = []
    setForecasts([])
    setForecastDetailsById({})
    setHistorySelectionId(null)
    setReadyForecastId(null)
    setForecastConfirmationOpen(false)
    refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionId])

  useEffect(() => {
    if (!currentUser?.id || !questionId) return undefined

    return subscribeToQuestionForecastStatus(questionId, {
      received(data) {
        const nextForecast = data?.forecast
        if (!nextForecast?.id) return

        mergeForecastState([nextForecast])
        if (nextForecast.status === "failed") {
          setError(nextForecast.error_message || "Forecast generation failed.")
        }
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id, questionId])

  const loadForecastDetail = async (forecastId) => {
    if (!forecastId || detailLoadingIds[forecastId] || forecastDetailsById[forecastId]) return

    setDetailLoadingIds((prev) => ({ ...prev, [forecastId]: true }))
    try {
      const data = await apiFetch(`/api/forecasts/${forecastId}`)
      setForecastDetailsById((prev) => ({ ...prev, [forecastId]: data.forecast || null }))
    } catch (e) {
      if (e.status !== 404) setError(e.message)
    } finally {
      setDetailLoadingIds((prev) => {
        const next = { ...prev }
        delete next[forecastId]
        return next
      })
    }
  }

  const activeOwnedForecast = useMemo(
    () => forecasts.find((forecast) => forecast.owned_by_current_user && FORECAST_IN_PROGRESS_STATUSES.has(forecast.status)) || null,
    [forecasts],
  )
  const readyForecast = useMemo(
    () => forecasts.find((forecast) => forecast.id === readyForecastId) || null,
    [forecasts, readyForecastId],
  )
  const forecastInFlight = starting || !!activeOwnedForecast

  useEffect(() => {
    if (activeOwnedForecast) setForecastConfirmationOpen(false)
  }, [activeOwnedForecast])

  useEffect(() => {
    if (!activeOwnedForecast) return undefined

    setElapsedTimeNowMs(Date.now())
    const intervalId = window.setInterval(() => setElapsedTimeNowMs(Date.now()), 1000)
    return () => window.clearInterval(intervalId)
  }, [activeOwnedForecast?.id])

  useEffect(() => {
    const previousActiveOwnedForecastId = previousActiveOwnedForecastIdRef.current
    const currentActiveOwnedForecastId = activeOwnedForecast?.id ?? null

    if (currentActiveOwnedForecastId) {
      setReadyForecastId(null)
    } else if (previousActiveOwnedForecastId) {
      const resolvedForecast = forecasts.find((forecast) => forecast.id === previousActiveOwnedForecastId)
      if (resolvedForecast?.status === "completed") setReadyForecastId(previousActiveOwnedForecastId)
    }

    previousActiveOwnedForecastIdRef.current = currentActiveOwnedForecastId
  }, [activeOwnedForecast, forecasts])

  useEffect(() => {
    if (!readyForecastId) return
    if (!readyForecast) setReadyForecastId(null)
  }, [readyForecast, readyForecastId])

  const startForecast = async () => {
    if (!question) return

    setStarting(true)
    try {
      const data = await apiFetch(`/api/questions/${question.id}/forecasts`, { method: "POST", body: {} })
      mergeForecastState([data.forecast])
      setForecastConfirmationOpen(false)
      if (data.user?.account_balance_cents != null) {
        onCurrentUserUpdate?.({ account_balance_cents: data.user.account_balance_cents })
      }
      setError("")
    } catch (e) {
      if (e.status === 409 && e.data?.code === "forecast_in_progress" && e.data?.forecast) {
        mergeForecastState([e.data.forecast])
        setForecastConfirmationOpen(false)
        setError("")
      } else {
        setError(e.message)
      }
    } finally {
      setStarting(false)
    }
  }

  const applyPublishedForecast = (publishedForecast) => {
    if (!publishedForecast?.id) return

    mergeForecastState([publishedForecast])
    setForecastDetailsById((prev) => ({
      ...prev,
      [publishedForecast.id]: publishedForecast,
    }))
  }

  const publishQuestion = async () => {
    if (!question?.can_publish || publishingQuestion) return
    if (!window.confirm(QUESTION_PUBLISH_CONFIRMATION)) return

    setPublishingQuestion(true)
    try {
      const data = await apiFetch(`/api/questions/${question.id}/publish`, { method: "PATCH", body: {} })
      if (data.question) setQuestion(data.question)
      setError("")
    } catch (e) {
      setError(e.message)
    } finally {
      setPublishingQuestion(false)
    }
  }

  const publishForecast = async (forecastId) => {
    if (!forecastId || publishingForecastIds[forecastId]) return
    if (!window.confirm(FORECAST_PUBLISH_CONFIRMATION)) return

    setPublishingForecastIds((prev) => ({ ...prev, [forecastId]: true }))
    try {
      const data = await apiFetch(`/api/forecasts/${forecastId}/publish`, { method: "PATCH", body: {} })
      applyPublishedForecast(data.forecast)
      setError("")
    } catch (e) {
      setError(e.message)
    } finally {
      setPublishingForecastIds((prev) => {
        const next = { ...prev }
        delete next[forecastId]
        return next
      })
    }
  }

  const latestCompleted = useMemo(() => forecasts.find((forecast) => forecast.status === "completed"), [forecasts])
  const selectedHistoryForecast = useMemo(() => {
    if (!historySelectionId) return null
    return forecasts.find((forecast) => forecast.id === historySelectionId) || null
  }, [forecasts, historySelectionId])
  const latestCompletedDetail = latestCompleted ? forecastDetailsById[latestCompleted.id] : null
  const selectedHistoryForecastDetail = selectedHistoryForecast ? forecastDetailsById[selectedHistoryForecast.id] : null

  useEffect(() => {
    if (!historySelectionId) return
    if (!forecasts.some((forecast) => forecast.id === historySelectionId)) setHistorySelectionId(null)
  }, [forecasts, historySelectionId])

  useEffect(() => {
    if (!latestCompleted?.id) return
    loadForecastDetail(latestCompleted.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestCompleted?.id])

  useEffect(() => {
    if (!selectedHistoryForecast?.id) return
    loadForecastDetail(selectedHistoryForecast.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedHistoryForecast?.id])

  const activeOwnedForecastElapsedMs = activeOwnedForecast
    ? Math.max(0, elapsedTimeNowMs - forecastStartedMs(activeOwnedForecast))
    : 0
  const forecastProgressFooter = useMemo(() => {
    if (!activeOwnedForecast) return null
    const forecastSeed = activeOwnedForecast.id || forecastStartedMs(activeOwnedForecast)
    return buildForecastProgressFooter(activeOwnedForecastElapsedMs, forecastSeed)
  }, [activeOwnedForecast, activeOwnedForecastElapsedMs])

  const renderForecastControls = (forecast, { includeViewLink = false } = {}) => {
    if (!forecast) return null

    return (
      <Group gap="xs">
        <VisibilityBadge publiclyVisible={forecast.publicly_visible} visibility={forecast.visibility} />
        {includeViewLink ? (
          <Button component={RouterLink} to={`/forecasts/${forecast.id}`} variant="subtle">
            View forecast
          </Button>
        ) : null}
        {forecast.can_publish ? (
          <Button
            type="button"
            variant="light"
            loading={!!publishingForecastIds[forecast.id]}
            onClick={() => publishForecast(forecast.id)}
          >
            Make public
          </Button>
        ) : null}
      </Group>
    )
  }

  if (loading && !question) return <LoadingState label="Loading question..." />
  if (notFound) return <NotFoundPage />

  return (
    <Stack gap="lg">
      <ErrorAlert message={error} />
      {activeOwnedForecast ? (
        <Alert color="blue" title="Forecast in progress" variant="light" data-testid="forecast-status-banner">
          <Stack gap={6}>
            <Text>Your forecast is generating. This usually takes roughly 15 minutes. The forecast will appear below when it is complete.</Text>
            <Text fw={600}>Elapsed time: {formatElapsedDuration(activeOwnedForecastElapsedMs)}</Text>
            {forecastProgressFooter ? (
              <Text
                size="sm"
                c="dimmed"
                data-testid="forecast-progress-footer"
                data-phase={forecastProgressFooter.phase}
              >
                {forecastProgressFooter.text}
              </Text>
            ) : null}
          </Stack>
        </Alert>
      ) : null}
      {!activeOwnedForecast && readyForecast ? (
        <Alert color="green" title="Forecast complete" variant="light" data-testid="forecast-status-banner">
          Your forecast is ready!
        </Alert>
      ) : null}

      {question ? (
        <>
          <PageHeader
            eyebrow="Question detail"
            variant="editorial"
            title="Question"
            subtitle="Review the current forecast, inspect forecast history, and request a new forecast from one place."
          />

          <Modal
            opened={forecastConfirmationOpen}
            onClose={() => {
              if (!starting) setForecastConfirmationOpen(false)
            }}
            title={hasEnoughBalance ? "Start forecast" : "Not enough funds"}
            centered
          >
            {hasEnoughBalance ? (
              <Stack gap="md" className="af-forecast-modal">
                <Text c="dimmed">{FORECAST_CONFIRMATION_MESSAGE}</Text>
                <Paper withBorder radius="xl" p="md" className="af-forecast-modal__summary">
                  <Text>
                    This forecast costs <Text span fw={600}>{forecastCostLabel}</Text>. That amount will be deducted from your balance as
                    soon as you start the forecast.
                  </Text>
                </Paper>
                <Group justify="flex-end">
                  <Button variant="default" onClick={() => setForecastConfirmationOpen(false)} disabled={starting}>
                    Cancel
                  </Button>
                  <Button onClick={startForecast} loading={starting}>
                    Forecast ({forecastCostLabel})
                  </Button>
                </Group>
              </Stack>
            ) : (
              <Stack gap="md" className="af-forecast-modal">
                <Paper withBorder radius="xl" p="md" className="af-forecast-modal__summary">
                  <Text>
                    You do not have enough funds on your account to generate a forecast. Forecasts cost{" "}
                    <Text span fw={600}>{forecastCostLabel}</Text>, and your current balance is <Text span fw={600}>{balanceLabel}</Text>.
                  </Text>
                </Paper>
                <Group justify="flex-end">
                  <Button variant="default" onClick={() => setForecastConfirmationOpen(false)}>
                    Close
                  </Button>
                  <Button component={RouterLink} to="/account/add-funds" onClick={() => setForecastConfirmationOpen(false)}>
                    Add Funds
                  </Button>
                </Group>
              </Stack>
            )}
          </Modal>

          <SectionCard
            variant="feature"
            title={
              <Stack gap={4}>
                <Title order={2}>{question.title}</Title>
                <Text size="sm" c="dimmed">
                  {formatDateTime(question.created_at)}
                </Text>
              </Stack>
            }
            action={(
              <Group gap="xs">
                <VisibilityBadge publiclyVisible={question.publicly_visible} visibility={question.visibility} />
                <Badge variant="light">{question.question_type}</Badge>
                {question.can_publish ? (
                  <Button type="button" variant="light" loading={publishingQuestion} onClick={publishQuestion}>
                    Make public
                  </Button>
                ) : null}
              </Group>
            )}
          >
            <QuestionDetails question={question} showTitle={false} />

            <Group justify="space-between" align="flex-start" gap="md">
              <Group gap="md" align="flex-start">
                {!currentUser ? (
                  <Button h={FORECAST_CTA_HEIGHT} disabled title="You must be logged in to make a forecast">
                    Forecast Now
                  </Button>
                ) : (
                  <Button
                    h={FORECAST_CTA_HEIGHT}
                    disabled={forecastInFlight}
                    title={
                      forecastInFlight
                        ? "A forecast is already generating."
                        : ""
                    }
                    onClick={() => setForecastConfirmationOpen(true)}
                  >
                    {starting ? "Starting..." : `Forecast Now (${forecastCostLabel})`}
                  </Button>
                )}
              </Group>
            </Group>
          </SectionCard>

          <SectionCard
            variant="editorial"
            title="Current forecast"
            subtitle="The most recently completed forecast on this question"
            action={renderForecastControls(latestCompleted, { includeViewLink: !!latestCompleted })}
          >
            {latestCompleted ? (
              <>
                <ForecastDisplay question={question} forecastData={latestCompleted.forecast_data} />
                {detailLoadingIds[latestCompleted.id] && !latestCompletedDetail ? <LoadingState label="Loading forecast details..." /> : null}
                {latestCompletedDetail ? (
                  <ForecastMainDetails
                    key={`current-forecast-details-${latestCompletedDetail.id}`}
                    writeUp={latestCompletedDetail.write_up}
                    calculationCode={latestCompletedDetail.calculation_code}
                    question={question}
                    contributingForecasts={latestCompletedDetail.contributing_forecasts}
                  />
                ) : null}
              </>
            ) : (
              <EmptyState message="No visible forecasts yet." />
            )}
          </SectionCard>

          <SectionCard variant="editorial" title="Forecast history" subtitle="Hover points to see values; click a point to preview that forecast.">
            {forecasts.length > 0 ? (
              <ForecastHistoryChart
                question={question}
                forecasts={forecasts}
                selectedForecastId={historySelectionId}
                onSelectForecastId={setHistorySelectionId}
              />
            ) : (
              <EmptyState message="No visible forecasts yet." />
            )}

            {selectedHistoryForecast ? (
              <Paper withBorder radius="xl" bg="gray.0" p="md" data-testid="selected-history-forecast" className="af-question-history-selection">
                <Stack gap="md">
                  <Group justify="space-between" align="flex-start" gap="md">
                    <Stack gap={4}>
                      <Text fw={600}>Selected forecast</Text>
                      <Text size="sm" c="dimmed">
                        {formatDateTime(selectedHistoryForecast.completed_at || selectedHistoryForecast.created_at)}
                      </Text>
                    </Stack>
                    <Group gap="xs">
                      {renderForecastControls(selectedHistoryForecast, { includeViewLink: true })}
                      <Button type="button" variant="subtle" onClick={() => setHistorySelectionId(null)}>
                        Clear
                      </Button>
                    </Group>
                  </Group>

                  <ForecastDisplay question={question} forecastData={selectedHistoryForecast.forecast_data} />
                  {detailLoadingIds[selectedHistoryForecast.id] && !selectedHistoryForecastDetail ? <LoadingState label="Loading forecast details..." /> : null}
                  {selectedHistoryForecastDetail ? (
                    <ForecastMainDetails
                      key={`selected-forecast-details-${selectedHistoryForecastDetail.id}`}
                      writeUp={selectedHistoryForecastDetail.write_up}
                      calculationCode={selectedHistoryForecastDetail.calculation_code}
                      question={question}
                      contributingForecasts={selectedHistoryForecastDetail.contributing_forecasts}
                    />
                  ) : null}
                </Stack>
              </Paper>
            ) : null}
          </SectionCard>
        </>
      ) : null}
    </Stack>
  )
}
